import { BasePlayer } from '../player/BasePlayer';
import { MsePlayer } from '../player/MsePlayer';
import { WebCodecsPlayer } from '../player/WebCodecsPlayer';
import { ControlMessage } from '../controlMessage/ControlMessage';
import { KeyCodeControlMessage } from '../controlMessage/KeyCodeControlMessage';
import { FeaturedInteractionHandler } from '../interactionHandler/FeaturedInteractionHandler';
import { KeyEventListener, KeyInputHandler } from '../googDevice/KeyInputHandler';
import { StreamClientLike } from '../streaming/StreamClientLike';
import { GoogMoreBox } from '../googDevice/toolbox/GoogMoreBox';
import { GoogToolBox } from '../googDevice/toolbox/GoogToolBox';
import DeviceMessage from '../googDevice/DeviceMessage';
import { KeyToCodeMap } from '../googDevice/KeyToCodeMap';
import Size from '../Size';
import VideoSettings from '../VideoSettings';
import ScreenInfo from '../ScreenInfo';
import Rect from '../Rect';
import store from '../../store';
import { resolveRtcRoute } from '../streaming/streamMode';

type WsRoomStartOptions = {
    udid: string;
    padCode?: string;
    pullUrl: string;
    deviceName?: string;
    player?: BasePlayer;
    fitToScreen?: boolean;
    videoSettings?: VideoSettings;
    show_ToolBox?: boolean;
    is_master_cloud?: boolean;
    sourceType?: 'single' | 'group_control';
    phoneId?: number;
};

const MAGIC_INITIAL = new TextEncoder().encode('scrcpy_initial');
const MAGIC_MESSAGE = DeviceMessage.MAGIC_BYTES_MESSAGE;
const DEVICE_NAME_FIELD_LENGTH = 64;
const TYPE_CHANGE_STREAM_PARAMETERS = 101;
const KEYFRAME_REQUEST_INTERVAL_MS = 1500;
const WS_ROOM_REFERENCE_BOUNDS_WIDTH = 1024;
const WS_ROOM_REFERENCE_BOUNDS_HEIGHT = 1920;
const WS_ROOM_REFERENCE_BITRATE = 5_000_000;
const WS_ROOM_REFERENCE_MAX_FPS = 30;
const WS_ROOM_REFERENCE_I_FRAME_INTERVAL = 5;
const RECONNECT_DELAY_MS = 1500;
const MAX_RECONNECT_ATTEMPTS = 5;
const VIDEO_STALL_TIMEOUT_MS = 25000;
const INITIAL_INFO_TIMEOUT_MS = 8000;
const MAX_INITIAL_INFO_RETRIES = 2;
const MAX_VIDEO_STALL_RECOVERY_ATTEMPTS = 2;
const TAG = '[WsRoomStreamClient]';

function startsWith(data: Uint8Array, prefix: Uint8Array): boolean {
    if (data.length < prefix.length) {
        return false;
    }
    for (let i = 0; i < prefix.length; i++) {
        if (data[i] !== prefix[i]) {
            return false;
        }
    }
    return true;
}

export class WsRoomStreamClient implements StreamClientLike, KeyEventListener {
    public readonly mode = 'ws_room' as const;
    public readonly phoneId: number;
    public readonly is_master_cloud: boolean;
    public player?: BasePlayer;

    private readonly udid: string;
    private pullUrl: string;
    private readonly padCode?: string;
    private readonly sourceType: 'single' | 'group_control';
    private readonly showToolBox: boolean;
    private fitToScreen?: boolean;
    private requestedVideoSettings?: VideoSettings;
    private ws?: WebSocket;
    private deviceName: string;
    private lastDisplayId = 0;
    private moreBox?: GoogMoreBox;
    private controlButtons?: HTMLElement;
    private deviceView?: HTMLElement;
    private videoHost?: HTMLElement;
    private touchHandler?: FeaturedInteractionHandler;
    private lastActivityTime = Date.now();
    private idleThreshold = 10000;
    private lastKeyframeRequestAtMs = 0;
    private remoteVideoWidth = 0;
    private remoteVideoHeight = 0;
    private activityCheckInterval?: number;
    private videoStallCheckInterval?: number;
    private activityListeners: Array<(isActive: boolean) => void> = [];
    private eventListeners: Array<{ element: EventTarget; type: string; handler: () => void }> = [];
    private lastVideoPacketAtMs = 0;
    private reconnectAttempts = 0;
    private reconnectTimer?: number;
    private isDisconnecting = false;
    private hasReceivedInitialInfo = false;
    private initialInfoTimer?: number;
    private initialInfoRetryCount = 0;
    private routeRefreshInProgress = false;
    private videoStallRecoveryAttempts = 0;

    public static create(options: WsRoomStartOptions): WsRoomStreamClient {
        return new WsRoomStreamClient(options);
    }

    private constructor(options: WsRoomStartOptions) {
        this.udid = options.udid;
        this.padCode = options.padCode;
        this.pullUrl = options.pullUrl;
        this.fitToScreen = options.fitToScreen;
        this.phoneId = options.phoneId ?? 0;
        this.is_master_cloud = options.is_master_cloud ?? false;
        this.sourceType = options.sourceType ?? 'single';
        this.showToolBox = options.show_ToolBox ?? true;
        this.deviceName = options.deviceName || options.udid;
        this.player = options.player ?? this.createDefaultPlayer(options.udid);
        this.startStream(options.videoSettings);
        document.body.classList.add('stream');
        this.connect();
    }

    private createDefaultPlayer(udid: string): BasePlayer {
        if (MsePlayer.isSupported()) {
            return new MsePlayer(udid);
        }
        return new WebCodecsPlayer(udid);
    }

    public addActivityListener(callback: (isActive: boolean) => void): void {
        this.activityListeners.push(callback);
        if (!this.activityCheckInterval) {
            this.startActivityCheck();
        }
        this.setupActivityListeners();
    }

    public removeActivityListener(callback: (isActive: boolean) => void): void {
        this.activityListeners = this.activityListeners.filter((item) => item !== callback);
        if (this.activityListeners.length === 0) {
            this.stopActivityCheck();
        }
    }

    public setIdleThreshold(ms: number): void {
        this.idleThreshold = ms;
    }

    public isUserActive(): boolean {
        return Date.now() - this.lastActivityTime <= this.idleThreshold;
    }

    public destroy(): void {
        this.disconnect();
    }

    public onDisconnected = (): void => {
        this.disconnect();
    };

    public getPlayer(): BasePlayer | undefined {
        return this.player;
    }

    public getDeviceName(): string {
        return this.deviceName;
    }

    public getMaxSize(): Size | undefined {
        if (!this.controlButtons) {
            return undefined;
        }
        const body = document.body;
        const width = (body.clientWidth - this.controlButtons.clientWidth) & ~15;
        const height = body.clientHeight & ~15;
        return new Size(width, height);
    }

    public sendMessage(message: ControlMessage): void {
        this.updateActivityTime();
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(message.toBuffer());
        }
    }

    public sendKeyEvent(code: string, action: 'down' | 'up', metaState: number): void {
        try {
            const mappedAction = action === 'down' ? 0 : 1;
            const androidKeyCode = KeyToCodeMap.get(code);
            if (typeof androidKeyCode !== 'number') {
                return;
            }
            const message = new KeyCodeControlMessage(mappedAction, androidKeyCode, 0, metaState);
            this.sendMessage(message);
        } catch (error) {
            console.error(`${TAG} Failed to send key event`, error);
        }
    }

    public onKeyEvent(event: KeyCodeControlMessage): void {
        this.sendMessage(event);
    }

    public sendBackspaceKey(): void {
        this.sendRoomJson({ keyCode: 67, touchType: 'keystroke' }, 'backspace');
    }

    public sendCharacter(char: string): void {
        if (!char) {
            return;
        }
        this.sendImeText(char);
    }

    public sendImeText(text: string): void {
        if (!text) {
            return;
        }
        this.sendRoomJson({ text, touchType: 'inputBox' }, 'ime_text');
    }

    /**
     * 把本地文本写入云机系统剪贴板（非粘贴到输入框）。
     * 推流端 CloudRtcServer.handleUserMessage 会命中 TOUCH_CLIPBOARD 分支，
     * 调 ClipboardManager.setPrimaryClip 写入云机剪贴板。
     */
    public sendClipboard(text: string): void {
        if (!text) {
            return;
        }
        this.sendRoomJson({ text, touchType: 'clipboard' }, 'clipboard');
    }

    private sendRoomJson(payload: Record<string, unknown>, reason: string): void {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn(`${TAG} Cannot send room json (${reason}): ws not ready`);
            return;
        }
        this.updateActivityTime();
        this.ws.send(JSON.stringify(payload));
    }

    public sendNewVideoSetting(videoSettings: VideoSettings): void {
        this.requestedVideoSettings = videoSettings;
        this.applyNewVideoSettings(videoSettings, false);
        this.sendCurrentVideoSettings('manual_update');
        this.requestKeyframe('manual_update');
    }

    public setHandleKeyboardEvents(enabled: boolean): void {
        if (enabled) {
            KeyInputHandler.addEventListener(this);
        } else {
            KeyInputHandler.removeEventListener(this);
        }
        store.commit('Update_Local_keyboard_flag', enabled);
    }

    private connect(): void {
        this.clearReconnectTimer();
        this.isDisconnecting = false;
        this.hasReceivedInitialInfo = false;
        this.initialInfoRetryCount = 0;
        this.videoStallRecoveryAttempts = 0;
        this.ws = new WebSocket(this.pullUrl);
        this.ws.binaryType = 'arraybuffer';
        this.ws.onopen = () => {
            console.log(`${TAG} WebSocket connected, sending initial video settings`);
            this.reconnectAttempts = 0;
            this.lastVideoPacketAtMs = Date.now();
            // Send initial video settings to trigger pusher to start streaming
            // The pusher needs this message to start, even if dimensions are not final
            this.sendCurrentVideoSettings('on_open_trigger');
            this.requestKeyframe('on_open_trigger');
            this.startVideoStallCheck();
            this.startInitialInfoTimeout();
        };
        this.ws.onmessage = (event: MessageEvent) => {
            if (typeof event.data === 'string') {
                this.handleTextMessage(event.data);
                return;
            }
            if (event.data instanceof ArrayBuffer) {
                this.handleBinaryMessage(new Uint8Array(event.data));
            }
        };
        this.ws.onclose = () => {
            this.stopActivityCheck();
            this.stopVideoStallCheck();
            this.scheduleReconnect('socket_closed');
        };
        this.ws.onerror = (error) => {
            console.error(`${TAG} ws_room socket error`, error);
        };
    }

    private handleTextMessage(text: string): void {
        try {
            const data = JSON.parse(text) as { type?: string; width?: number; height?: number; key?: string; data?: unknown };
            if (data.type === 'video_config') {
                const sizeChanged =
                    !!data.width &&
                    !!data.height &&
                    (data.width !== this.remoteVideoWidth || data.height !== this.remoteVideoHeight);
                this.updateRemoteState(data.width, data.height, 'video_config');

                // Treat video_config as initial info if we haven't received it yet
                if (!this.hasReceivedInitialInfo && data.width && data.height) {
                    console.log(`${TAG} Using video_config as initial info`);
                    this.clearInitialInfoTimer();
                    this.hasReceivedInitialInfo = true;
                    this.initialInfoRetryCount = 0;
                    this.sendCurrentVideoSettings('video_config_initial');
                    this.requestKeyframe('video_config_initial');
                } else if (sizeChanged) {
                    this.requestKeyframe(`video_config_${data.width || 0}x${data.height || 0}`);
                }
                return;
            }
            // 推流端在云机剪贴板变化时会发 {"key":"clipboard","data":"{\"content\":\"xxx\"}"}
            // 见 cloud_yunjin CloudRtcServer.kt clipboardListener
            if (data.key === 'clipboard') {
                this.handleRemoteClipboard(data.data);
                return;
            }
        } catch (error) {
            console.warn(`${TAG} Ignored text message`, text, error);
        }
    }

    private handleRemoteClipboard(payload: unknown): void {
        let content: string | undefined;
        if (typeof payload === 'string') {
            try {
                const parsed = JSON.parse(payload) as { content?: unknown };
                if (typeof parsed.content === 'string') content = parsed.content;
            } catch (_e) {
                // 兼容推流端直接把文本塞进 data 的老版本
                content = payload;
            }
        } else if (payload && typeof payload === 'object') {
            const asObj = payload as { content?: unknown };
            if (typeof asObj.content === 'string') content = asObj.content;
        }
        if (!content) return;
        try {
            if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                navigator.clipboard.writeText(content).catch((error) => {
                    console.warn(`${TAG} writeText failed, fallback to execCommand`, error);
                    this.writeClipboardViaExecCommand(content as string);
                });
            } else {
                this.writeClipboardViaExecCommand(content);
            }
        } catch (error) {
            console.warn(`${TAG} Failed to sync remote clipboard`, error);
        }
    }

    private writeClipboardViaExecCommand(text: string): void {
        try {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'fixed';
            textarea.style.top = '-10000px';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            document.execCommand('copy');
            textarea.remove();
        } catch (error) {
            console.warn(`${TAG} execCommand copy failed`, error);
        }
    }

    private handleBinaryMessage(data: Uint8Array): void {
        if (startsWith(data, MAGIC_INITIAL)) {
            this.parseInitialInfo(data);
            return;
        }
        if (startsWith(data, MAGIC_MESSAGE)) {
            this.handleDeviceMessage(data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength));
            return;
        }
        if (!this.player) {
            return;
        }
        // Mark as received initial info when we get video data
        if (!this.hasReceivedInitialInfo) {
            console.log(`${TAG} Received video data before initial info, treating as initialized`);
            this.clearInitialInfoTimer();
            this.hasReceivedInitialInfo = true;
            this.initialInfoRetryCount = 0;
        }
        this.videoStallRecoveryAttempts = 0;
        if (this.player.getState() === BasePlayer.STATE.PAUSED) {
            this.player.play();
        }
        if (this.player.getState() === BasePlayer.STATE.PLAYING) {
            this.lastVideoPacketAtMs = Date.now();
            this.player.pushFrame(this.normalizeVideoPacket(data));
        }
    }

    private parseInitialInfo(data: Uint8Array): void {
        try {
            this.clearInitialInfoTimer();
            this.hasReceivedInitialInfo = true;
            this.initialInfoRetryCount = 0;
            this.videoStallRecoveryAttempts = 0;
            const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
            let offset = MAGIC_INITIAL.length;
            const nameBytes = data.slice(offset, offset + DEVICE_NAME_FIELD_LENGTH);
            offset += DEVICE_NAME_FIELD_LENGTH;
            const deviceName = new TextDecoder()
                .decode(nameBytes)
                .replace(/\0+$/g, '')
                .trim();
            if (deviceName) {
                this.deviceName = deviceName;
            }
            const displaysCount = view.getInt32(offset, false);
            offset += 4;
            for (let i = 0; i < displaysCount; i++) {
                const displayId = view.getInt32(offset, false);
                offset += 4;
                const width = view.getInt32(offset, false);
                offset += 4;
                const height = view.getInt32(offset, false);
                offset += 4;
                offset += 4 * 4;
                this.lastDisplayId = displayId;
                const screenInfoLength = view.getInt32(offset, false);
                offset += 4;
                if (screenInfoLength > 0 && offset + screenInfoLength <= data.length) {
                    const screenInfoView = new DataView(
                        data.buffer,
                        data.byteOffset + offset,
                        screenInfoLength,
                    );
                    const videoWidth = screenInfoView.getInt32(16, false);
                    const videoHeight = screenInfoView.getInt32(20, false);
                    this.updateRemoteState(videoWidth, videoHeight, `initial_display_${displayId}`);
                } else {
                    this.updateRemoteState(width, height, `initial_display_${displayId}`);
                }
                offset += Math.max(screenInfoLength, 0);
                const videoSettingsLength = view.getInt32(offset, false);
                offset += 4 + Math.max(videoSettingsLength, 0);
            }
            console.log(`${TAG} Received initial info, sending video settings`);
            this.sendCurrentVideoSettings('initial_info');
            this.requestKeyframe('initial_info');
        } catch (error) {
            console.error(`${TAG} Failed to parse initial info`, error);
        }
    }

    private handleDeviceMessage(data: ArrayBuffer): void {
        const message = DeviceMessage.fromBuffer(data);
        if (this.moreBox) {
            this.moreBox.OnDeviceMessage(message);
        }
    }

    private updateRemoteState(width?: number, height?: number, reason = 'remote_update'): void {
        if (!width || !height || !this.player) {
            return;
        }
        const sizeChanged = this.remoteVideoWidth !== width || this.remoteVideoHeight !== height;
        this.remoteVideoWidth = width;
        this.remoteVideoHeight = height;
        if (sizeChanged) {
            this.player.setScreenInfo(new ScreenInfo(new Rect(0, 0, width, height), new Size(width, height), 0));
            this.syncLayoutToVideoSize(width, height);
        }
        if (sizeChanged && !this.requestedVideoSettings) {
            this.sendCurrentVideoSettings(`remote_size_update:${reason}`);
        }
    }

    private sendCurrentVideoSettings(reason: string): void {
        const videoSettings = this.player?.getVideoSettings();
        if (!videoSettings || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn(`${TAG} Cannot send video settings: ws=${!!this.ws} readyState=${this.ws?.readyState} videoSettings=${!!videoSettings}`);
            return;
        }
        // Match the Android viewer profile that is already verified for ws_room.
        const bounds = new Size(WS_ROOM_REFERENCE_BOUNDS_WIDTH, WS_ROOM_REFERENCE_BOUNDS_HEIGHT);
        const buffer = new ArrayBuffer(36);
        const view = new DataView(buffer);
        let offset = 0;
        view.setUint8(offset, TYPE_CHANGE_STREAM_PARAMETERS);
        offset += 1;
        view.setInt32(offset, Math.max(videoSettings.bitrate || 0, WS_ROOM_REFERENCE_BITRATE), false);
        offset += 4;
        view.setInt32(offset, Math.max(videoSettings.maxFps || 0, WS_ROOM_REFERENCE_MAX_FPS), false);
        offset += 4;
        view.setUint8(offset, WS_ROOM_REFERENCE_I_FRAME_INTERVAL);
        offset += 1;
        view.setUint16(offset, bounds.width, false);
        offset += 2;
        view.setUint16(offset, bounds.height, false);
        offset += 2;
        view.setUint16(offset, 0, false);
        offset += 2;
        view.setUint16(offset, 0, false);
        offset += 2;
        view.setUint16(offset, 0, false);
        offset += 2;
        view.setUint16(offset, 0, false);
        offset += 2;
        view.setUint8(offset, videoSettings.sendFrameMeta ? 1 : 0);
        offset += 1;
        view.setInt8(offset, videoSettings.lockedVideoOrientation);
        offset += 1;
        view.setInt32(offset, this.lastDisplayId, false);
        offset += 4;
        view.setInt32(offset, 0, false);
        offset += 4;
        view.setInt32(offset, 0, false);
        this.ws.send(buffer);
        console.log(
            `${TAG} Sent video settings: reason=${reason} bounds=${bounds.width}x${bounds.height} displayId=${this.lastDisplayId} remoteSize=${this.remoteVideoWidth}x${this.remoteVideoHeight}`,
        );
    }

    private requestKeyframe(reason: string): void {
        const now = Date.now();
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn(`${TAG} Cannot request keyframe: ws=${!!this.ws} readyState=${this.ws?.readyState}`);
            return;
        }
        if (now - this.lastKeyframeRequestAtMs < KEYFRAME_REQUEST_INTERVAL_MS) {
            console.debug(`${TAG} Skipping keyframe request (throttled): reason=${reason}`);
            return;
        }
        this.lastKeyframeRequestAtMs = now;
        this.ws.send(
            JSON.stringify({
                type: 'request_keyframe',
                reason,
            }),
        );
        console.log(`${TAG} Requested keyframe: reason=${reason}`);
    }

    private normalizeVideoPacket(data: Uint8Array): Uint8Array {
        if (data.length >= 3 && data[0] === 0 && data[1] === 0 && data[2] === 1) {
            const normalized = new Uint8Array(data.length + 1);
            normalized[0] = 0;
            normalized.set(data, 1);
            return normalized;
        }
        return data;
    }

    private startStream(videoSettings?: VideoSettings): void {
        const player = this.player;
        if (!player) {
            throw new Error('Player is required for ws_room');
        }
        this.touchHandler = new FeaturedInteractionHandler(
            player,
            this as never,
            this.udid,
            'default',
            this.sourceType,
            this.is_master_cloud,
        );
        const deviceView = document.createElement('div');
        deviceView.className = 'device-view';
        this.deviceView = deviceView;
        const stop = () => {
            const parent = deviceView.parentElement;
            if (parent) {
                parent.removeChild(deviceView);
            }
            this.disconnect();
            player.stop();
        };
        const googMoreBox = (this.moreBox = new GoogMoreBox(this.udid, player, this as never));
        const moreBox = googMoreBox.getHolderElement();
        googMoreBox.setOnStop(stop);
        if (this.showToolBox) {
            const googToolBox = GoogToolBox.createToolBox(this.udid, player, this as never, moreBox, {
                hideKeyboard: true,
            });
            this.controlButtons = googToolBox.getHolderElement();
            deviceView.appendChild(this.controlButtons);
        }
        const video = document.createElement('div');
        video.className = 'video';
        this.videoHost = video;
        deviceView.appendChild(video);
        deviceView.appendChild(moreBox);
        player.setParent(video);
        player.pause();

        const appElement = document.getElementById(`phone-display-${this.udid}`);
        if (!appElement) {
            throw new Error(`Could not find container for ${this.udid}`);
        }
        appElement.innerHTML = '';
        appElement.style.position = 'relative';
        appElement.style.width = '100%';
        appElement.style.height = '100%';
        appElement.style.display = 'flex';
        appElement.style.flexDirection = 'column';
        appElement.style.alignItems = 'center';
        appElement.style.justifyContent = 'flex-start';
        appElement.style.overflow = 'hidden';
        appElement.appendChild(deviceView);

        const nextVideoSettings = videoSettings || player.getVideoSettings();
        this.applyNewVideoSettings(nextVideoSettings, false);
        this.setupActivityListeners();
    }

    private applyNewVideoSettings(videoSettings: VideoSettings, saveToStorage: boolean): void {
        let fitToScreen = false;
        if (videoSettings.bounds && videoSettings.bounds.equals(this.getMaxSize())) {
            fitToScreen = true;
        }
        this.player?.setVideoSettings(videoSettings, fitToScreen, saveToStorage);
    }

    private disconnect(): void {
        this.isDisconnecting = true;
        this.clearReconnectTimer();
        this.clearInitialInfoTimer();
        this.stopActivityCheck();
        this.stopVideoStallCheck();
        this.cleanupEventListeners();
        this.activityListeners = [];
        this.remoteVideoWidth = 0;
        this.remoteVideoHeight = 0;
        this.hasReceivedInitialInfo = false;
        this.touchHandler?.release();
        this.touchHandler = undefined;
        KeyInputHandler.removeEventListener(this);
        if (this.ws) {
            try {
                this.ws.onclose = null;
                this.ws.onerror = null;
                this.ws.onmessage = null;
                this.ws.onopen = null;
                this.ws.close();
            } catch (_error) {
                // Ignore close failures during teardown.
            }
            this.ws = undefined;
        }
    }

    private startVideoStallCheck(): void {
        this.stopVideoStallCheck();
        this.videoStallCheckInterval = window.setInterval(() => {
            if (this.isDisconnecting || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
                return;
            }
            if (!this.lastVideoPacketAtMs) {
                this.lastVideoPacketAtMs = Date.now();
                return;
            }
            const stalledForMs = Date.now() - this.lastVideoPacketAtMs;
            if (stalledForMs < VIDEO_STALL_TIMEOUT_MS) return;

            // 分级恢复，避免一上来就重发 video_settings 引起 pusher 重置编码器 → 画面闪屏：
            //  1) 第一次：仅请关键帧（requestKeyframe），解码器不重置，用户几乎察觉不到
            //  2) 第二次：重发 video_settings + 请关键帧（可能仍有短暂解码闪烁）
            //  3) 超出次数：走 reconnect（最后手段，会黑一下）
            if (this.videoStallRecoveryAttempts < MAX_VIDEO_STALL_RECOVERY_ATTEMPTS) {
                this.videoStallRecoveryAttempts += 1;
                const reason = `video_stall_recovery_${this.videoStallRecoveryAttempts}`;
                if (this.videoStallRecoveryAttempts === 1) {
                    console.warn(`${TAG} video stalled ${stalledForMs}ms → soft recovery (keyframe only)`);
                    this.requestKeyframe(reason);
                } else {
                    console.warn(`${TAG} video stalled ${stalledForMs}ms → hard recovery (resend settings)`);
                    this.sendCurrentVideoSettings(reason);
                    this.requestKeyframe(reason);
                }
                this.lastVideoPacketAtMs = Date.now();
                return;
            }
            console.warn(`${TAG} video stalled ${stalledForMs}ms after recovery attempts, reconnecting`);
            this.reconnectSocket('video_stalled');
        }, 2000);
    }

    private stopVideoStallCheck(): void {
        if (this.videoStallCheckInterval) {
            clearInterval(this.videoStallCheckInterval);
            this.videoStallCheckInterval = undefined;
        }
    }

    private scheduleReconnect(reason: string): void {
        if (this.isDisconnecting || this.reconnectTimer) {
            return;
        }
        if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            console.warn(`${TAG} reconnect aborted after max attempts, reason=${reason}`);
            return;
        }
        this.reconnectAttempts += 1;
        this.reconnectTimer = window.setTimeout(() => {
            this.reconnectTimer = undefined;
            console.warn(`${TAG} reconnecting ws_room, reason=${reason}, attempt=${this.reconnectAttempts}`);
            this.connect();
        }, RECONNECT_DELAY_MS);
    }

    private reconnectSocket(reason: string): void {
        if (!this.ws || this.ws.readyState === WebSocket.CLOSING) {
            return;
        }
        this.stopVideoStallCheck();
        try {
            this.ws.close(4000, reason);
        } catch (error) {
            console.warn(`${TAG} reconnect close failed`, error);
            this.scheduleReconnect(reason);
        }
    }

    private clearReconnectTimer(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = undefined;
        }
    }

    private startInitialInfoTimeout(): void {
        this.clearInitialInfoTimer();
        this.initialInfoTimer = window.setTimeout(() => {
            if (!this.hasReceivedInitialInfo && this.ws && this.ws.readyState === WebSocket.OPEN) {
                if (this.initialInfoRetryCount < MAX_INITIAL_INFO_RETRIES) {
                    this.initialInfoRetryCount += 1;
                    const reason = `initial_info_retry_${this.initialInfoRetryCount}`;
                    console.warn(
                        `${TAG} No initial info received after ${INITIAL_INFO_TIMEOUT_MS}ms, retrying handshake (${this.initialInfoRetryCount}/${MAX_INITIAL_INFO_RETRIES})`,
                    );
                    this.sendCurrentVideoSettings(reason);
                    this.requestKeyframe(reason);
                    this.startInitialInfoTimeout();
                    return;
                }
                console.warn(`${TAG} No initial info received after retries, refreshing route and reconnecting`);
                this.refreshRouteAndReconnect('no_initial_info');
            }
        }, INITIAL_INFO_TIMEOUT_MS);
    }

    private async refreshRouteAndReconnect(reason: string): Promise<void> {
        if (this.routeRefreshInProgress) {
            return;
        }
        this.routeRefreshInProgress = true;
        try {
            if (this.padCode) {
                const route = await resolveRtcRoute(this.padCode);
                if (route.mode === 'ws_room' && route.pullUrl) {
                    this.pullUrl = route.pullUrl;
                    console.warn(`${TAG} Refreshed ws_room route before reconnect: reason=${reason}`);
                }
            }
        } catch (error) {
            console.warn(`${TAG} Refresh ws_room route failed, reconnecting with existing url`, error);
        } finally {
            this.routeRefreshInProgress = false;
            this.reconnectSocket(reason);
        }
    }

    private clearInitialInfoTimer(): void {
        if (this.initialInfoTimer) {
            clearTimeout(this.initialInfoTimer);
            this.initialInfoTimer = undefined;
        }
    }

    private startActivityCheck(): void {
        this.stopActivityCheck();
        this.activityCheckInterval = window.setInterval(() => {
            this.notifyListeners(this.isUserActive());
        }, 1000);
    }

    private stopActivityCheck(): void {
        if (this.activityCheckInterval) {
            clearInterval(this.activityCheckInterval);
            this.activityCheckInterval = undefined;
        }
    }

    private notifyListeners(isActive: boolean): void {
        this.activityListeners.forEach((listener) => {
            try {
                listener(isActive);
            } catch (error) {
                console.error(`${TAG} Activity listener error`, error);
            }
        });
    }

    private updateActivityTime(): void {
        const wasActive = this.isUserActive();
        this.lastActivityTime = Date.now();
        if (!wasActive && this.isUserActive()) {
            this.notifyListeners(true);
        }
    }

    private setupActivityListeners(): void {
        this.cleanupEventListeners();
        const element = this.player?.getTouchableElement();
        if (!element) {
            return;
        }
        const handler = () => this.updateActivityTime();
        const events = ['touchstart', 'touchmove', 'touchend', 'mousedown', 'mousemove', 'click', 'scroll'];
        events.forEach((type) => {
            element.addEventListener(type, handler, { passive: true });
            this.eventListeners.push({ element, type, handler });
        });
    }

    private cleanupEventListeners(): void {
        this.eventListeners.forEach(({ element, type, handler }) => {
            element.removeEventListener(type, handler);
        });
        this.eventListeners = [];
    }

    private syncLayoutToVideoSize(width: number, height: number): void {
        if (!this.player || !this.videoHost || !this.deviceView) {
            return;
        }
        this.videoHost.style.width = `${width}px`;
        this.videoHost.style.height = `${height}px`;
        this.videoHost.style.flex = '0 0 auto';
        this.videoHost.style.overflow = 'hidden';

        const touchLayer = this.player.getTouchableElement();
        touchLayer.style.width = `${width}px`;
        touchLayer.style.height = `${height}px`;
        touchLayer.style.pointerEvents = 'auto';

        const mediaLayer = this.videoHost.querySelector('.video-layer, video, canvas') as HTMLElement | null;
        if (mediaLayer) {
            mediaLayer.style.width = `${width}px`;
            mediaLayer.style.height = `${height}px`;
            mediaLayer.style.maxWidth = '100%';
            mediaLayer.style.maxHeight = '100%';
        }

        this.deviceView.style.maxWidth = '100%';
        this.deviceView.style.maxHeight = '100%';
        this.deviceView.style.alignItems = 'center';
    }
}
